import React, { useMemo, useState } from 'react'
import '../../../styles/analytics/StudentAnalytics.css'
import { detailedEmotionTrendsDummy } from '../../../constants/StudentAnalyticsConstants'
import { emotions_heads } from '../../../constants/Constants'
import { ReactComponent as BigArrow } from '../../../assets/svg/BigArrow.svg'
import { formatDateI18n, getXLabelsNew } from '../../../utils/util'
import { LoadingContainer } from './CheckInWidget'
import { NoBarResults } from './BarChartDetails'
import { useTranslation } from 'react-i18next'

import { ReactComponent as Happy } from '../../../assets/svg/GreenSmiley.svg'
import { ReactComponent as Sad } from '../../../assets/svg/RedSad.svg'
import i18n from '../../../i18n'

const transformedData = detailedEmotionTrendsDummy?.map((item) => {
    const total = item.comfortable + item.uncomfortable
    const percentage_comfortable = total > 0 ? (item.comfortable / total) * 100 : 0
    const percentage_uncomfortable = total > 0 ? (item.uncomfortable / total) * 100 : 0

    return {
        id: item._id,
        label_name: item.label_name,
        comfortable: item.comfortable,
        uncomfortable: item.uncomfortable,
        total_emotions: item.total_emotions,
        percentage_comfortable: parseFloat(percentage_comfortable.toFixed(2)),
        percentage_uncomfortable: parseFloat(percentage_uncomfortable.toFixed(2)),
    }
})

function BarChartDetailsOverall({ height = 272, data, loading, student, daysFilter }) {
    const showLoading = useMemo(() => loading || !data || !data?.length, [loading, data])

    const usedData = useMemo(() => {
        return data == null ? transformedData : data
    }, [data])

    const allValues = usedData?.map((e) => Math.max(e?.percentage_comfortable, e?.percentage_uncomfortable, 1)) ?? []
    const maxValue = 100
    const xLabels = getXLabelsNew(maxValue)
    const numberOfHorizontalLines = xLabels.length
    const horizontalLines = Array(numberOfHorizontalLines)?.fill('1')
    return (
        <div className="relative">
            <DataContainer
                height={height}
                horizontalLines={horizontalLines}
                maxValue={maxValue}
                newList={usedData}
                blur={showLoading}
                student={student}
                xLabels={xLabels}
                daysFilter={daysFilter}
            />
            {showLoading && <LoadingContainer height={height} showEmptyData={!loading && (!data || !data?.length)} />}
        </div>
    )
}

export default BarChartDetailsOverall

const DataContainer = ({ height, horizontalLines, newList, maxValue, blur, student, xLabels, daysFilter }) => {
    const { t } = useTranslation()

    const [hoveredComfortable, setHoveredComfortable] = useState(null)
    const [hoveredUncomfortable, setHoveredUncomfortable] = useState(null)

    const highestComfortableItem = useMemo(() => {
        if (!newList?.length) return null

        return newList.reduce((maxItem, currentItem) => {
            const currentScore = currentItem?.percentage_comfortable * (currentItem?.comfortable * 1.0001)
            const maxScore = maxItem?.percentage_comfortable * (maxItem?.comfortable * 1.0001)

            return currentScore > maxScore ? currentItem : maxItem
        }, newList[0])
    }, [newList])

    const highestUncomfortableItem = useMemo(() => {
        if (!newList?.length) return null
        return newList.reduce((maxItem, currentItem) => {
            const currentScore = currentItem?.percentage_uncomfortable * (currentItem?.uncomfortable * 1.0001)
            const maxScore = maxItem?.percentage_uncomfortable * (maxItem?.uncomfortable * 1.0001)

            return currentScore > maxScore ? currentItem : maxItem
        }, newList[0])
    }, [newList])

    const highestComfortableLabel = useMemo(() => {
        if (daysFilter.numberOfDays == 7) return t('highestComfortableLabelDay', { date: formatDateI18n(highestComfortableItem?.label_name, i18n.language) })
        else if (daysFilter.numberOfDays <= 91) return t('highestComfortableLabelWeek', { date: highestComfortableItem?.label_name })
        else return t('highestComfortableLabelMonth', { date: t(highestComfortableItem?.label_name) })
    }, [daysFilter, highestComfortableItem, t])

    const highestUncomfortableLabel = useMemo(() => {
        if (daysFilter.numberOfDays == 7) return t('highestUncomfortableLabelDay', { date: formatDateI18n(highestUncomfortableItem?.label_name, i18n.language) })
        else if (daysFilter.numberOfDays <= 91) return t('highestUncomfortableLabelWeek', { date: highestUncomfortableItem?.label_name })
        else return t('highestUncomfortableLabelMonth', { date: t(highestUncomfortableItem?.label_name) })
    }, [daysFilter, highestUncomfortableItem, t])

    return (
        <div className={`${blur ? 'blur' : ''}`}>
            <div className={`flex flex-row mt-5 w-full pb-[44px] `} style={{ minHeight: height }}>
                <div className="min-w-[30px] relative z-[1]">
                    <div className="flex flex-col-reverse  gap-[1px] justify-between h-full min-w-[30px] ">
                        {xLabels?.map((item, index) => (
                            <div className={`flex flex-col w-full`} style={{ minHeight: 20 }} key={index}>
                                <p className="text-[12px] leading-5 text-darkenPurple font-medium text-end">{item}%</p>
                            </div>
                        ))}
                    </div>
                    <div className="flex flex-col items-center justify-center  w-[60px] absolute -bottom-[38px] h-11">
                        <p className="text-[12px] leading-5 text-[#77768D] text-center font-medium tracking-tighter">{t('Check-ins')}</p>
                        <p className="text-[12px] leading-5 text-[#77768D] text-center font-medium tracking-tighter">{t('Time frame')}</p>
                    </div>
                </div>
                <div className="flex-1 relative ml-[10px]" style={{ scrollbarColor: '#9392AD transparent' }}>
                    <div
                        className="h-full py-[9px]"
                        style={{
                            minWidth: '100%',
                        }}
                    >
                        <div className="flex flex-col items-center w-full gap-[1px] h-full  justify-between " style={{ minHeight: height }}>
                            {horizontalLines?.map((item, index) => (
                                <div key={index} className={`border-dashed border-b border-b-[#ECEBF8] w-full `} style={{ height: 1, width: '100%' }} />
                            ))}
                        </div>
                    </div>
                    <div className="flex flex-row items-end absolute top-0 left-0 z-[3] w-full justify-between py-[10px] gap-[14px]">
                        {newList?.map((item, index) => (
                            <BarChartItem
                                height={height}
                                item={item}
                                maxValue={maxValue}
                                key={index}
                                index={index}
                                student={student}
                                hoveredComfortable={item.label_name == hoveredComfortable}
                                hoveredUncomfortable={item.label_name == hoveredUncomfortable}
                            />
                        ))}
                    </div>
                </div>
            </div>
            <div className="flex flex-row gap-4 mt-4">
                {!!highestComfortableItem?.percentage_comfortable && (
                    <div
                        className="flex-1 flex flex-row items-center justify-between gap-4 rounded-[12px] bg-[#6FCC8766] px-3 py-[9px] max-w-[50%] "
                        onMouseEnter={() => setHoveredComfortable(highestComfortableItem?.label_name)}
                        onMouseLeave={() => setHoveredComfortable(null)}
                    >
                        <div>
                            <Happy height={40} width={40} />
                        </div>
                        <p className="text-[14px] leading-[20px] font-semibold text-darkenPurple">{highestComfortableLabel}</p>
                        <div className={`${false ? 'rotate-0' : 'rotate-180'} transition`}>
                            <BigArrow color={`${false ? '#FC4C59' : '#6FCC87'}`} />
                        </div>
                    </div>
                )}
                {!!highestUncomfortableItem?.percentage_uncomfortable && (
                    <div
                        className="flex-1 flex flex-row items-center justify-between gap-4 rounded-[12px] bg-[#FD9AA133] px-3 py-[9px] max-w-[50%] "
                        onMouseEnter={() => setHoveredUncomfortable(highestUncomfortableItem?.label_name)}
                        onMouseLeave={() => setHoveredUncomfortable(null)}
                    >
                        <div>
                            <Sad height={40} width={40} />
                        </div>
                        <p className="text-[14px] leading-[20px] font-semibold text-darkenPurple ">{highestUncomfortableLabel}</p>
                        <div className={`${true ? 'rotate-0' : 'rotate-180'} transition`}>
                            <BigArrow color={`${true ? '#FC4C59' : '#6FCC87'}`} />
                        </div>
                    </div>
                )}
            </div>
        </div>
    )
}

const BarChartItem = ({ height, item, maxValue, index, hoveredComfortable, hoveredUncomfortable }) => {
    const hovered = hoveredComfortable || hoveredUncomfortable
    return (
        <div className="min-w-[30px] max-w-[88px] relative  flex-1" style={{ marginLeft: index === 0 ? 40 : 0 }}>
            <div className="w-full flex flex-col justify-end relative " style={{ minHeight: height, height: height }}>
                {!!item.total_emotions && (
                    <div className="flex flex-row gap-[2px] flex-1 items-end h-full">
                        <SingleBar
                            valueColor="#389E52"
                            color={hoveredComfortable ? '#6FCC87E6' : '#6FCC8799'}
                            value={item?.percentage_comfortable}
                            maxValue={maxValue}
                            id={`comfortable-${item?.label_name}`}
                            emotions={item?.comfortableEmotions}
                            focused={hoveredComfortable}
                        />
                        <SingleBar
                            valueColor="#FD9AA1"
                            color={hoveredUncomfortable ? '#FC4C59E6' : '#FC4C5999'}
                            value={item?.percentage_uncomfortable}
                            maxValue={maxValue}
                            id={`uncomfortable-${item?.label_name}`}
                            emotions={item?.uncomfortableEmotions}
                            focused={hoveredUncomfortable}
                        />
                    </div>
                )}
                {!item.total_emotions && <NoBarResults height={height} />}
            </div>
            <div className=" w-full h-[44px] flex flex-col items-center justify-center pt-1">
                <div className="w-full flex flex-row justify-center gap-1">
                    <p
                        className={`flex-1 text-[12px] leading-5 font-medium transition-all duration-500  text-center ${
                            hoveredComfortable ? 'text-[#413F5E]' : 'text-[#77768D]'
                        } tracking-tighter h-5`}
                    >
                        {item.comfortable ?? ''}
                    </p>
                    <p
                        className={`flex-1 text-[12px] leading-5  transition-all font-medium duration-500 text-center ${
                            hoveredUncomfortable ? 'text-[#413F5E]' : 'text-[#77768D]'
                        } tracking-tighter h-5`}
                    >
                        {item.uncomfortable ?? ''}
                    </p>
                </div>
                <p
                    className={`text-[10px] xl:text-[12px] transition-all leading-5 font-medium duration-500 text-center ${
                        hovered ? 'text-[#413F5E]' : 'text-[#77768D]'
                    } tracking-tighter whitespace-nowrap`}
                >
                    {item?.label_name}
                </p>
            </div>
        </div>
    )
}

const SingleBar = ({ color, value, maxValue, id, valueColor, emotions = [], focused }) => {
    const { t } = useTranslation()

    const [hovered, setHovered] = useState(false)

    return (
        <div
            className={`flex-1 rounded-t-[4px] rounded-b-[2px] min-h-[5px] relative transition-all duration-[0.5s]`}
            style={{ backgroundColor: color, height: `${(value / (maxValue ?? 1)) * 100}%` }}
        >
            <div className="absolute -top-5 w-full flex items-center justify-center">
                <p className={`font-semibold leading-5 text-[10px] xl:text-[12px] transition-all`} style={{ color: valueColor }}>
                    {Math.round(value)}%
                </p>
            </div>
            {!!emotions.length && (
                <div
                    className="h-full flex transition-all duration-500 flex-col justify-between items-center px-2 py-2 gap-1 overflow-hidden"
                    onMouseEnter={() => setHovered(true)}
                    onMouseLeave={() => setHovered(false)}
                    style={{ opacity: hovered ? 1 : 0 }}
                >
                    {emotions?.map((emotion, index) => (
                        <div key={index} className="flex-1 min-h-0 flex items-center">
                            <img src={emotions_heads[emotion.emotion]} alt={emotion.emotion} title={t(emotion.emotion)} className="w-full h-full object-contain" />
                        </div>
                    ))}
                </div>
            )}
        </div>
    )
}
